import { mcpErrorMessage, type MCPApiError as ApiError } from "../errors";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  agentToolsApi,
  type CreateMCPConnectionPayload,
  type CreateMCPConnectionResponse,
} from "../../../services/agent-tools-service";
import { mcpQueryKeys } from "../query-keys";
import { canNotifyMCPMutation, useScopedMCPMutation } from "./useScopedMCPMutation";
import { mcpRequestOptions } from "./useMCPQueryScope";

type Options = {
  onSuccess?: (response: CreateMCPConnectionResponse) => void;
};

export const useCreateMCPConnection = (options: Options = {}) => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  return useScopedMCPMutation<CreateMCPConnectionResponse, CreateMCPConnectionPayload, undefined, ApiError>({
    mutationFn: (payload, scope) =>
      agentToolsApi.createMCPConnection(payload, mcpRequestOptions(scope)),
    onSuccess: (response, _payload, scope) => {
      // OAuth toast/redirect is handled by the page so it can synchronously
      // open the new tab from the form-submit click (popup blockers reject
      // window.open calls scheduled after async work).
      if (!response.oauth_init && canNotifyMCPMutation(scope)) {
        toast.success(t("mcpPage.mutation.added"));
      }
      queryClient.invalidateQueries({ queryKey: mcpQueryKeys.lists(scope.key) });
      if (canNotifyMCPMutation(scope)) options.onSuccess?.(response);
    },
    onError: (error: ApiError, _payload, _context, scope) => {
      if (!canNotifyMCPMutation(scope, error)) return;
      toast.error(
        mcpErrorMessage(
          error,
          t("mcpPage.mutation.addFailed"),
          i18n.resolvedLanguage ?? i18n.language,
          t("mcpPage.errors.connectionExists"),
        ),
      );
    },
  });
};
